'use client';

import type { ComponentProps } from 'react';
import React, { useCallback, useState } from 'react';
import { Button, Flex, Spinner, Text } from '@skin-seoul/design-system';
import { CART_QUERY_KEYS, ProductType, useCartService, useRegionCurrency } from '@skin-seoul/service';
import { useProductCard } from '../ProductCardContext';
import { LucideIcon } from '../../../components/LucideIcon';
import { AnimatePresence, motion } from 'motion/react';
import { BridgeApi, Condition, useDebouncedNumberCommit, useIsTouchDevice, useMutationLoading, usePageLogger } from '@skin-seoul/utils';
const MotionFlex = motion.create(Flex);
const MotionButton = motion.create(Button);
type ProductCardAddToCartProps = ComponentProps<typeof MotionFlex>;
const ProductCardAddToCart = (props: ProductCardAddToCartProps) => {
  const {
    sku,
    productType,
    href,
    name,
    brand,
    originalPrice,
    salePrice,
    productId,
    category
  } = useProductCard();
  const {
    currency
  } = useRegionCurrency();
  const {
    useAddToCart,
    useGetCartQuery,
    useUpdateCartItem,
    useDeleteCartItem
  } = useCartService();
  const {
    data: cart
  } = useGetCartQuery({
    currency
  });
  const {
    mutate: addToCart,
    isPending: isAddToCartPending
  } = useAddToCart({
    currency
  });
  const {
    mutate: updateCartItem,
    isPending: isUpdateCartItemPending
  } = useUpdateCartItem({
    currency
  });
  const {
    mutate: deleteCartItem,
    isPending: isDeleteCartItemPending
  } = useDeleteCartItem({
    currency
  });
  const [hovered, setHovered] = useState(false);
  const isTouch = useIsTouchDevice();
  const [tappedOpen, setTappedOpen] = useState(false);
  const isCartLoading = useMutationLoading(CART_QUERY_KEYS.default());
  const logger = usePageLogger();
  const currentQuantity = cart?.cart?.item?.find(item => item.sku === sku)?.quantity || 0;
  const isLoading = isAddToCartPending || isUpdateCartItemPending || isDeleteCartItemPending;
  const COMMIT_DEBOUNCE_MS = 300;
  const {
    value: displayQuantity,
    updateBy
  } = useDebouncedNumberCommit({
    initialValue: currentQuantity,
    delayMs: COMMIT_DEBOUNCE_MS,
    max: 999,
    onCommit: nextQuantity => {
      if (!sku || productType === ProductType.Variable) {
        return;
      }
      const itemKey = cart?.cart?.item?.find(item => item.sku === sku)?.key;
      if (nextQuantity <= 0) {
        if (itemKey) {
          deleteCartItem({
            itemKey,
            currency
          }, {
            onSuccess: () => {
              if (BridgeApi.isWebview()) {
                BridgeApi.cartUpdated();
              }
            }
          });
        }
        return;
      }
      if (!itemKey) {
        addToCart({
          sku,
          quantity: nextQuantity,
          currency
        }, {
          onSuccess: () => {
            if (BridgeApi.isWebview()) {
              BridgeApi.cartUpdated();
            }
          }
        });
        return;
      }
      updateCartItem({
        itemKey,
        quantity: nextQuantity,
        currency
      }, {
        onSuccess: () => {
          if (BridgeApi.isWebview()) {
            BridgeApi.cartUpdated();
          }
        }
      });
    }
  });
  const openOptions = !isTouch && hovered || displayQuantity > 0 || isLoading || isTouch && tappedOpen;
  const trackViewItemEvent = useCallback(() => {
    if (productType !== ProductType.Variable || !logger) {
      return;
    }
    const itemPrice = salePrice ?? originalPrice;
    logger.event('PLC_view_item', 'click', {
      name: 'PLC_view_item',
      'x-fb-cd-content_type': 'product',
      'x-fb-cd-content_name': name,
      'x-fb-cd-content_ids': productId || sku,
      value: itemPrice,
      currency,
      items: [{
        item_id: productId || sku,
        item_name: name,
        item_brand: brand || '',
        item_sku: sku,
        price: itemPrice.toString(),
        item_category: category?.[0] || '',
        item_category2: category?.[1] || ''
      }],
      event_name: 'view_item',
      event_time: Math.round(new Date().getTime() / 1000)
    });
  }, [productType, logger, salePrice, originalPrice, productId, sku, name, brand, category, currency]);
  const trackAddToCartEvent = useCallback((quantity: number) => {
    if (!logger) {
      return;
    }
    const itemPrice = salePrice ?? originalPrice;
    const totalPrice = itemPrice * quantity;
    logger.click('add_to_cart', {
      'x-fb-cd-content_type': 'product',
      'x-fb-cd-content_name': name,
      'x-fb-cd-content_ids': productId || sku,
      value: totalPrice.toString(),
      currency,
      items: [{
        item_id: productId || sku,
        item_name: name,
        item_brand: brand || '',
        item_sku: sku,
        price: itemPrice.toString(),
        item_category: category?.[0] || '',
        item_category2: category?.[1] || ''
      }],
      event_name: 'add_to_cart',
      event_time: Math.round(new Date().getTime() / 1000),
      product_sku: sku
    });
  }, [logger, salePrice, originalPrice, name, productId, sku, currency, brand, category]);
  const handleNavigate = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    e?.preventDefault();
    window.location.href = href;
  }, [href]);
  const handleAddClick = useCallback((e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!sku || isLoading || productType === ProductType.Variable) {
      return;
    }
    trackAddToCartEvent(1);
    updateBy(1);
  }, [sku, isLoading, productType, updateBy, trackAddToCartEvent]);
  const handleMinusClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!sku || isLoading) {
      return;
    }
    updateBy(-1);
  }, [sku, isLoading, updateBy]);
  const handlePlusClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const isVariableProduct = productType === ProductType.Variable;
    const isFirstTapOnMobile = isTouch && !tappedOpen;
    const isSecondTapOnMobile = isTouch && tappedOpen;
    const shouldNavigateOnDesktop = !isTouch && openOptions;
    if (!isVariableProduct) {
      handleAddClick(e);
      return;
    }
    if (isFirstTapOnMobile) {
      e?.stopPropagation();
      e?.preventDefault();
      setTappedOpen(true);
      return;
    }
    if (isSecondTapOnMobile || shouldNavigateOnDesktop) {
      trackViewItemEvent();
      handleNavigate(e);
    }
  }, [productType, isTouch, tappedOpen, openOptions, handleAddClick, handleNavigate, trackViewItemEvent]);
  return <Flex justify="flex-end" width="100%" height="fit-content" data-sentry-element="Flex" data-sentry-component="ProductCardAddToCart" data-sentry-source-file="ProductCardAddToCart.tsx">
      <MotionFlex onHoverStart={() => !isTouch && setHovered(true)} onHoverEnd={() => {
      setHovered(false);
      if (isTouch) {
        setTappedOpen(false);
      }
    }} width="100%" alignItems="center" {...props} data-sentry-element="MotionFlex" data-sentry-source-file="ProductCardAddToCart.tsx">
        <MotionFlex key="expanded" justifyContent="flex-end" alignItems="center" width="100%" gap={2} backgroundColor={openOptions ? 'primary.75' : 'transparent'} borderRadius="20px" data-sentry-element="MotionFlex" data-sentry-source-file="ProductCardAddToCart.tsx">
          <AnimatePresence initial={false} mode="popLayout" data-sentry-element="AnimatePresence" data-sentry-source-file="ProductCardAddToCart.tsx">
            {openOptions && <MotionFlex key="minus-group" alignItems="center" gap={2} w="100%" justifyContent="space-between" initial={{
            x: 48,
            opacity: 0
          }} animate={{
            x: 0,
            opacity: 1
          }} exit={{
            x: 48,
            opacity: 0
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 36,
            mass: 0.6
          }}>
                <Condition expression={productType === ProductType.Variable} then={<Text textStyle="label.small" paddingLeft={{
              base: 5
            }} color="primary.400" fontWeight="regular" textAlign="center" w="100%">
                      Available Options
                    </Text>} else={<>
                      <MotionButton onClick={handleMinusClick} height="40px" width="40px" bg="primary.400" color="white" borderRadius="20px" px={3} boxShadow="0 4px 4px 0 rgba(111, 63, 215, 0.25)" disabled={isLoading || isCartLoading}>
                        <LucideIcon boxSize={4} name="Minus" color="white" />
                      </MotionButton>
                      <Condition expression={isLoading} then={<Flex w="100%" justifyContent="center" alignItems="center">
                            <Spinner size="sm" color="primary.500" animationDuration="0.8s" />
                          </Flex>} else={<Text textStyle="label.medium" color="neutral.700" w="100%" textAlign="center" fontWeight="regular">
                            {displayQuantity}
                          </Text>} />
                    </>} />
              </MotionFlex>}
          </AnimatePresence>

          <Button onClick={handlePlusClick} height="40px" width="40px" bg="primary.400" color="white" borderRadius="20px" px={3} boxShadow="0 4px 4px 0 rgba(111, 63, 215, 0.25)" disabled={isLoading || isCartLoading} data-sentry-element="Button" data-sentry-source-file="ProductCardAddToCart.tsx">
            <LucideIcon boxSize={4} name="Plus" color="white" data-sentry-element="LucideIcon" data-sentry-source-file="ProductCardAddToCart.tsx" />
          </Button>
        </MotionFlex>
      </MotionFlex>
    </Flex>;
};
export default ProductCardAddToCart;